<template>
  <span
    ref="triggerRef"
    class="tt-trigger"
    @mouseenter="onOpen()"
    @mouseleave="onClose()"
    @focusin="onOpen()"
    @focusout="onClose()"
    @keydown.escape.prevent.stop="forceClose()"
  >
    <slot name="trigger">
      <img
        src="/images/icon/info-circle-icon.svg"
        alt="info-circle-icon"
        style="width: 18px; height: 18px; cursor: help"
        v-bind="props"
      />
    </slot>
  </span>

  <teleport to="body">
    <transition name="tt-fade">
      <div
        v-if="open"
        ref="tooltipRef"
        class="tt"
        role="tooltip"
        :aria-hidden="!open"
        :style="tooltipStyle"
        @mouseenter="onOpen(true)"
        @mouseleave="onClose(true)"
      >
        <div class="tt-content">
          <slot name="content">
            {{ text }}
          </slot>
        </div>
        <div
          class="tt-arrow"
          :class="`tt-arrow--${actualPlacement}`"
          :style="arrowStyle"
        />
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
  defineProps,
} from "vue";

const props = defineProps({
  text: { type: String, default: "" },
  placement: {
    type: String,
    default: "top",
    validator: (v) => ["top", "bottom", "left", "right"].includes(v),
  },
  showDelay: { type: Number, default: 80 },
  hideDelay: { type: Number, default: 80 },
  offset: { type: Number, default: 8 },
  viewportPadding: { type: Number, default: 8 },
});

const triggerRef = ref(null);
const tooltipRef = ref(null);

const open = ref(false);
let showTimer = null;
let hideTimer = null;

const state = reactive({
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  actualPlacement: props.placement,
  arrowX: 0,
  arrowY: 0,
});

const ARROW_SIZE = 10;
const ARROW_PAD = 10;

function onOpen(fromTooltip = false) {
  clearTimeout(hideTimer);
  if (open.value) return;
  showTimer = setTimeout(
    async () => {
      open.value = true;
      await nextTick();
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        reposition();
      });
    },
    fromTooltip ? 0 : props.showDelay
  );
}

function onClose(fromTooltip = false) {
  clearTimeout(showTimer);
  hideTimer = setTimeout(
    () => {
      open.value = false;
    },
    fromTooltip ? Math.min(props.hideDelay, 40) : props.hideDelay
  );
}

function forceClose() {
  clearTimeout(showTimer);
  clearTimeout(hideTimer);
  open.value = false;
}

function getRects() {
  const t = triggerRef.value;
  const f = tooltipRef.value;
  if (!t || !f) return null;
  const triggerRect = t.getBoundingClientRect();
  const tooltipRect = f.getBoundingClientRect();
  return { triggerRect, tooltipRect };
}

function fits(placement, triggerRect, tooltipRect, vp, pad, gap) {
  switch (placement) {
    case "top":
      return triggerRect.top >= tooltipRect.height + pad + gap;
    case "bottom":
      return vp.height - triggerRect.bottom >= tooltipRect.height + pad + gap;
    case "left":
      return triggerRect.left >= tooltipRect.width + pad + gap;
    case "right":
      return vp.width - triggerRect.right >= tooltipRect.width + pad + gap;
  }
}

function computeCoords(placement, triggerRect, tooltipRect, vp, pad, gap) {
  let top = 0,
    left = 0;
  const cx = triggerRect.left + triggerRect.width / 2;
  const cy = triggerRect.top + triggerRect.height / 2;

  if (placement === "top") {
    top = triggerRect.top - tooltipRect.height - gap;
    left = cx - tooltipRect.width / 2;
  } else if (placement === "bottom") {
    top = triggerRect.bottom + gap;
    left = cx - tooltipRect.width / 2;
  } else if (placement === "left") {
    left = triggerRect.left - tooltipRect.width - gap;
    top = cy - tooltipRect.height / 2;
  } else {
    left = triggerRect.right + gap;
    top = cy - tooltipRect.height / 2;
  }

  left = Math.min(Math.max(left, pad), vp.width - tooltipRect.width - pad);
  top = Math.min(Math.max(top, pad), vp.height - tooltipRect.height - pad);

  const arrowX = cx - left;
  const arrowY = cy - top;

  return { top, left, arrowX, arrowY };
}

function choosePlacement(triggerRect, tooltipRect) {
  const vp = { width: window.innerWidth, height: window.innerHeight };
  const pad = props.viewportPadding;
  const gap = props.offset;

  const order = (() => {
    const p = props.placement;
    const opposite = {
      top: "bottom",
      bottom: "top",
      left: "right",
      right: "left",
    }[p];
    const remaining = ["top", "bottom", "left", "right"].filter(
      (x) => x !== p && x !== opposite
    );

    const space = {
      top: triggerRect.top - pad - gap,
      bottom: vp.height - triggerRect.bottom - pad - gap,
      left: triggerRect.left - pad - gap,
      right: vp.width - triggerRect.right - pad - gap,
    };
    remaining.sort((a, b) => (space[b] ?? 0) - (space[a] ?? 0));
    return /** @type {('top'|'bottom'|'left'|'right')[]} */ ([
      p,
      opposite,
      ...remaining,
    ]);
  })();

  for (const pl of order) {
    if (fits(pl, triggerRect, tooltipRect, vp, pad, gap)) return pl;
  }
  const candidates = ["top", "bottom", "left", "right"];
  const score = {
    top: triggerRect.top,
    bottom: vp.height - triggerRect.bottom,
    left: triggerRect.left,
    right: vp.width - triggerRect.right,
  };
  candidates.sort((a, b) => score[b] - score[a]);
  return /** @type {'top'|'bottom'|'left'|'right'} */ (candidates[0]);
}

function clampArrow(x, max) {
  return Math.min(Math.max(x, ARROW_PAD), max - ARROW_PAD);
}

function reposition() {
  if (isRepositioning) return;
  isRepositioning = true;

  const rects = getRects();
  if (!rects) {
    isRepositioning = false;
    return;
  }
  const { triggerRect, tooltipRect } = rects;
  const vp = { width: window.innerWidth, height: window.innerHeight };

  const chosen = choosePlacement(triggerRect, tooltipRect);
  const { top, left, arrowX, arrowY } = computeCoords(
    chosen,
    triggerRect,
    tooltipRect,
    vp,
    props.viewportPadding,
    props.offset
  );

  const ax = clampArrow(arrowX, tooltipRect.width);
  const ay = clampArrow(arrowY, tooltipRect.height);

  state.top = Math.round(top);
  state.left = Math.round(left);
  state.width = tooltipRect.width;
  state.height = tooltipRect.height;
  state.actualPlacement = chosen;
  state.arrowX = ax;
  state.arrowY = ay;

  requestAnimationFrame(() => {
    isRepositioning = false;
  });
}

let roTrigger = null;
let roTooltip = null;
let rafId = null;
let isRepositioning = false;

const onScrollOrResize = () => {
  if (open.value) {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      reposition();
    });
  }
};

const handleResizeObserver = () => {
  if (open.value && !isRepositioning) {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      reposition();
    });
  }
};

const handleError = (event) => {
  if (
    event.message?.includes?.("ResizeObserver loop") ||
    event.message?.includes?.("ResizeObserver loop completed")
  ) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return false;
  }
};

let originalConsoleError = null;

onMounted(() => {
  originalConsoleError = console.error;
  console.error = (...args) => {
    const message = args[0]?.toString?.() || "";
    if (
      message.includes("ResizeObserver loop") ||
      message.includes("ResizeObserver loop completed")
    ) {
      return;
    }
    originalConsoleError.apply(console, args);
  };

  window.addEventListener("error", handleError, true);
  window.addEventListener("scroll", onScrollOrResize, {
    passive: true,
    capture: true,
  });
  window.addEventListener("resize", onScrollOrResize, { passive: true });
  roTrigger = new ResizeObserver(handleResizeObserver);
  roTooltip = new ResizeObserver(handleResizeObserver);
  if (triggerRef.value) roTrigger.observe(triggerRef.value);
});

onBeforeUnmount(() => {
  if (originalConsoleError) {
    console.error = originalConsoleError;
  }
  window.removeEventListener("error", handleError, true);
  window.removeEventListener("scroll", onScrollOrResize, { capture: true });
  window.removeEventListener("resize", onScrollOrResize);
  if (rafId) cancelAnimationFrame(rafId);
  roTrigger?.disconnect();
  roTooltip?.disconnect();
  clearTimeout(showTimer);
  clearTimeout(hideTimer);
});

watch(open, async (val) => {
  if (val) {
    await nextTick();
    if (tooltipRef.value) roTooltip.observe(tooltipRef.value);
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      reposition();
    });
  } else {
    roTooltip?.disconnect();
    if (rafId) cancelAnimationFrame(rafId);
  }
});

const tooltipStyle = computed(() => ({
  top: state.top + "px",
  left: state.left + "px",
  "--tt-arrow-size": ARROW_SIZE + "px",
}));

const ARROW_INSET = 1;

const arrowStyle = computed(() => {
  const base = {
    left: state.arrowX + "px",
    top: state.arrowY + "px",
  };

  switch (state.actualPlacement) {
    case "top":
      base.top = state.height - ARROW_INSET + "px";
      break;
    case "bottom":
      base.top = ARROW_INSET + "px";
      break;
    case "left":
      base.left = state.width - ARROW_INSET + "px";
      break;
    case "right":
      base.left = ARROW_INSET + "px";
      break;
  }
  return base;
});

const actualPlacement = computed(() => state.actualPlacement);
</script>

<style scoped>
.tt-trigger {
  display: inline-block;
  line-height: 0;
}

.tt {
  position: fixed;
  z-index: 9999999999;
  max-width: 340px;
  pointer-events: none;
  background: #ffffff;
  color: rgba(59, 70, 72, 1);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.tt-content {
  pointer-events: none;
}

.tt-arrow {
  position: absolute;
  width: var(--tt-arrow-size);
  height: var(--tt-arrow-size);
  transform: translate(-50%, -50%) rotate(45deg);
  background: #ffffff;
}

.tt-arrow--top {
  border-left: 1px solid #ffffff;
  border-top: 1px solid #ffffff;
}

.tt-arrow--bottom {
  border-right: 1px solid #ffffff;
  border-bottom: 1px solid #ffffff;
}

.tt-arrow--left {
  border-left: 1px solid #ffffff;
  border-bottom: 1px solid #ffffff;
}

.tt-arrow--right {
  border-right: 1px solid #ffffff;
  border-top: 1px solid #ffffff;
}

.tt-fade-enter-active,
.tt-fade-leave-active {
  transition:
    opacity 120ms ease,
    transform 120ms ease;
}
.tt-fade-enter-from,
.tt-fade-leave-to {
  opacity: 0;
  transform: translateY(2px) scale(0.98);
}
</style>
